import {
  buildClientDevice,
  publishOrtb2,
  publisherDomain,
  toOrtb2,
} from '@redbrickmedia/duplex.user-identity'
import { z } from 'zod'
import debug from '../../common/utils/console'

const logger = debug.withTag('plugins:filters')

export const UserFilteringSchema = z.object({
  country: z.string(),
  region: z.string().optional(),
  regionName: z.string().optional(),
  city: z.string().default(''),
  postalCode: z.string().optional(),
  metroCode: z.string().optional(),
  continent: z.string().optional(),
  timezone: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  userAgent: z.string().nullable(),
  isMobile: z.boolean().default(false),
  os: z.string().optional(),
})

export type UserFilteringData = z.infer<typeof UserFilteringSchema>

const defaultUserFilteringData = UserFilteringSchema.parse({
  country: 'US',
  userAgent: null,
  isMobile: false,
})

type ClientDevice = ReturnType<typeof buildClientDevice>

function articleIdOf(route: { params: Record<string, unknown> }) {
  const id = route.params.id
  return (Array.isArray(id) ? id[0] : id) ?? null
}

/**
 * Hands the geo and device we just resolved to Prebid as ORTB first-party data.
 *
 * Queued rather than published directly: Assertive Yield applies its own Prebid
 * config once on load and the queue drains after, so anything set before that
 * would be overwritten.
 */
function publishFirstPartyData(geo: UserFilteringData, device: ClientDevice) {
  const domain = publisherDomain()
  if (!domain) {
    return
  }

  const { siteName } = useRuntimeConfig().public
  const name = typeof siteName === 'string' && siteName ? siteName : domain

  const publish = (articleId: string | null) => {
    try {
      const ortb2 = toOrtb2(
        { geo, device },
        { domain, name, page: window.location.href },
        articleId ? { id: articleId } : undefined
      )

      window.assertiveQueue = window.assertiveQueue || []
      window.assertiveQueue.push(() => publishOrtb2(ortb2))
    } catch (error) {
      // Ads must load whether or not first-party data does.
      logger.warn('Failed to publish ORTB first-party data', error)
    }
  }

  publish(articleIdOf(useRoute()))

  // Articles arrive by client-side navigation. publishOrtb2 replaces
  // site.content rather than appending.
  useRouter().afterEach((to) => publish(articleIdOf(to)))
}

/**
 * Fetches user filtering data from the server and provides it to the Nuxt app,
 * then publishes it into the bid stream.
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  const device = buildClientDevice()

  try {
    const url = `/api/v2/filters`
    const geo = await $fetch<Record<string, unknown>>(url)

    const filters = {
      ...geo,
      userAgent: device.ua ?? null,
      isMobile: device.isMobile,
      ...(device.os ? { os: device.os } : {}),
    }
    const parsedFilters = UserFilteringSchema.safeParse(filters)
    if (!parsedFilters.success) {
      logger.warn('Invalid user filtering data:', parsedFilters.error)
      nuxtApp.provide('filters', defaultUserFilteringData)
      publishFirstPartyData(defaultUserFilteringData, device)
      return
    }

    nuxtApp.provide('filters', parsedFilters.data)
    publishFirstPartyData(parsedFilters.data, device)
  } catch (error) {
    logger.warn('Error fetching user filtering data:', error)
    // Geo is gone, but the device half was read locally and still holds.
    const fallback = {
      ...defaultUserFilteringData,
      userAgent: device.ua ?? null,
      isMobile: device.isMobile,
    }
    nuxtApp.provide('filters', fallback)
    publishFirstPartyData(fallback, device)
  }
})
